import { DefaultPage } from '@atoms/defaultPage/style';
import { useProcesses } from '@hooks/useProcesses';
import type { ProcessModel } from '@models/process.model';
import { CircularProgress } from '@mui/joy';
import { observer } from 'mobx-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { LoaderBox, LoaderContainer } from '@/components/ui/loader';
import { cn } from '@/lib/utils';
import { PROCESS_TAG_CONFIG, ProcessTag } from '@/types/process.types';

import EmptyProcess from './emptyProcess';
import { ViewType } from './home.utils';
import HomeHeader from './homeHeader';
import ProcessCard from './processCard';

const TAG_FILTERS = [
  { value: null, label: 'All' },
  ...Object.values(ProcessTag).map(tag => ({ value: tag, label: tag }))
] as const;

const Home = () => {
  const { processes, loading } = useProcesses();
  const [filter, setFilter] = useState('');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [view, setView] = useState<ViewType>(
    (localStorage.getItem('viewType') as ViewType) ?? ViewType.Grid
  );

  useEffect(() => {
    localStorage.setItem('numberOfProcesses', processes.length.toString());
  }, [processes]);

  if (loading) {
    return (
      <LoaderContainer>
        <LoaderBox>
          <CircularProgress />
        </LoaderBox>
      </LoaderContainer>
    );
  }

  const filteredProcess = processes
    .filter(p =>
      p.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
    )
    .filter(p => {
      if (tagFilter === null) {
        return true;
      }
      return p.tag === tagFilter;
    });

  const draftProcesses = filteredProcess.filter(p => p.draft);
  const normalProcesses = filteredProcess.filter(p => !p.draft);

  const onProcessSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  };

  const renderProcessCards = (processesToRender: ProcessModel[]) =>
    [...processesToRender]
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      )
      .map((process: ProcessModel) => (
        <ProcessCard
          key={process.id}
          loading={false}
          process={process}
          view={view}
        />
      ));

  const onViewChange = (newView: ViewType) => {
    setView(newView);
    localStorage.setItem('viewType', newView);
  };

  if (!loading && processes.length === 0) {
    return <EmptyProcess />;
  }

  const hasAnyTags = processes.some(p => p.tag);

  const renderTagFilters = () => {
    if (!hasAnyTags) {
      return null;
    }

    return (
      <div className="flex items-center gap-2 overflow-x-auto">
        {TAG_FILTERS.map(({ value, label }) => (
          <button
            className={cn(
              'shrink-0 rounded-full border px-3 py-1 font-medium text-xs transition-colors',
              tagFilter === value
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-muted-foreground hover:bg-muted'
            )}
            key={label}
            onClick={() => setTagFilter(value)}
            type="button"
          >
            {value === null ? label : PROCESS_TAG_CONFIG[value].label}
          </button>
        ))}
      </div>
    );
  };

  const renderProcesses = () => {
    if (normalProcesses.length > 0) {
      return (
        <>
          <h2 className="font-semibold text-xl">Processes</h2>
          <div
            className={cn(
              'grid transition-all duration-200',
              view === ViewType.Grid
                ? 'grid-cols-[repeat(auto-fill,minmax(min(300px,100%),1fr))] gap-6'
                : 'grid-cols-1 gap-3'
            )}
          >
            {renderProcessCards(normalProcesses)}
          </div>
        </>
      );
    }
    return null;
  };

  const renderDraft = () => {
    if (draftProcesses.length > 0) {
      return (
        <>
          <h2 className="font-semibold text-xl">Drafts</h2>
          <div
            className={cn(
              'grid transition-all duration-200',
              view === ViewType.Grid
                ? 'grid-cols-[repeat(auto-fill,minmax(min(300px,100%),1fr))] gap-6'
                : 'grid-cols-1 gap-3'
            )}
          >
            {renderProcessCards(draftProcesses)}
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <DefaultPage>
      <div className="flex w-full max-w-[1150px] flex-col gap-8">
        <HomeHeader
          onProcessSearch={onProcessSearch}
          onViewChange={onViewChange}
          selectedView={view}
        />
        {renderTagFilters()}
        {renderDraft()}
        {renderProcesses()}
      </div>
    </DefaultPage>
  );
};

export default observer(Home);
